import * as store from '@/store';
import sdk from '@/sdk';
import {isNull} from 'lodash-es';
import {useCallback, useEffect, useState} from 'react';
import {DocRole, MemberType, ResignStatus} from '@/Constant';
import {UserInfoCard} from './UserInfoCard';
import {reportQuickAuthorizeBtn, reportQuickAuthorizePopper} from '@/buried';
import {useAuthorizeRead, useCanAuthorizeRead} from '@/hooks/authorize';

interface IEditorUserInfoCard extends Partial<User>{
    type: string;
    from?: string;
    usersMap?: Map<string, User>;
    showPermissionBtn: boolean | null;
    setTooltipVisible?: (visible: boolean) => void;
    docUrl?: string;
}


const EditorUserInfoCard = ({
    userGuid,
    nickname,
    email,
    department,
    usersMap,
    from,
    showPermissionBtn = null,
    setTooltipVisible,
    docUrl,
}: IEditorUserInfoCard) => {

    const [user, setUser] = useState<Partial<User> | null>(null);
    const [showAddPermission, setShowAddPermission] = useState<boolean>(false);
    const [isShowCoinDocList, setIsShowCoinDocList] = useState<boolean>(false);
    const canAuthorizeRead = useCanAuthorizeRead();
    const authorizeRead = useAuthorizeRead();

    const getUserPermission = useCallback(async (user: User, options: {from?: string} = {}) => {
        const userGuid = sdk.as.user?.userGuid;
        const result = {
            reading: true,
        };
        const repository = store.getData(store.KEYS.CUR_REPOSITORY);
        const {docGuid} = store.getData(store.KEYS.CUR_DOCUMENT) || {};
        if (await canAuthorizeRead(repository, docGuid, user.userGuid)) {
            result.reading = false;
        }

        reportQuickAuthorizePopper({
            accuser_id: userGuid,
            page_id: docGuid,
            kb_id: repository.repositoryGuid,
            source: options.from === 'comment' ? 1 : 0,
            suser_id: user.userGuid,
            type: 1,
        });

        return result;
    }, [canAuthorizeRead]);

    useEffect(() => {
        if (user && user.userGuid === userGuid) {
            return;
        }
        // 因用户信息数据会被缓存 所以当小流量数据未返回前 按照小流量获取
        const userInfo = sdk.as.getUserInfo(userGuid || '', true);
        if (userInfo) {
            setUser(userInfo);
        } else {
            setUser(null);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userGuid, usersMap]);

    useEffect(() => {
        // 如果需要展示授权按钮 则在接口请求后 控制文档列表和授权按钮的展示
        // - 没有查看权限 展示授权按钮 不展示文档列表
        // - 有查看权限 不展示授权按钮 展示文档列表
        if (showPermissionBtn) {
            user && getUserPermission(user as User, {
                from,
            }).then(({reading}) => {
                setShowAddPermission(!reading);
                setIsShowCoinDocList(reading);
            }).catch(() => {
                setIsShowCoinDocList(true);
            });
        } else {
            setIsShowCoinDocList(true);
        }

    }, [user, getUserPermission, from, showPermissionBtn]);

    const handleGetPermission = useCallback(async () => {
        try {
            const userGuid = sdk.as.user?.userGuid;
            const repository = store.getData(store.KEYS.CUR_REPOSITORY);
            const {docGuid} = store.getData(store.KEYS.CUR_DOCUMENT) || {};
            reportQuickAuthorizeBtn({
                accuser_id: userGuid,
                page_id: docGuid,
                kb_id: repository.repositoryGuid,
                source: from === 'comment' ? 1 : 0,
                suser_id: user?.userGuid,
                type: 1,
            });

            await authorizeRead(repository, docGuid, [
                {
                    nickname: user?.nickname,
                    memberType: MemberType.User,
                    memberGuid: user?.userGuid,
                },
            ], DocRole.Reader);
            setShowAddPermission(false);
            setTooltipVisible && setTooltipVisible(false);
        } catch (e) {
            console.error(e);
        }
    }, [authorizeRead, from, setTooltipVisible, user?.nickname, user?.userGuid]);

    return <UserInfoCard
        nickname={nickname}
        department={department}
        email={email}
        userGuid={userGuid}
        {...user}
        isDimission={user?.resignStatus === ResignStatus.noOnJob}
        isShowAddPermission={showAddPermission}
        isShowCoinDocList={isShowCoinDocList}
        onGetPermission={handleGetPermission}
        docUrl={docUrl}
    />;
};

export default store.connect([
    store.KEYS.USERS_MAP,
])(EditorUserInfoCard);
